import { useEffect, useState } from "react"

export const useLocalStorage = <T>(
  key: string,
  initialValue: T,
  watch = false
) => {
  const [state, setState] = useState(() => {
    // Initialize the state
    try {
      if (typeof window === "undefined") {
        return initialValue
      }

      const value = window.localStorage.getItem(key)
      // Check if the local storage already has any values,
      // otherwise initialize it with the passed initialValue
      return value ? JSON.parse(value) : initialValue
    } catch (error) {
      console.log(error)
      return initialValue
    }
  })

  const setValue = (value: T) => {
    try {
      if (typeof window === "undefined") {
        return
      }

      // If the passed value is a callback function,
      //  then call it with the existing state.
      const valueToStore = value instanceof Function ? value(state) : value

      // Emit a custom event so other hooks can listen to the change
      const event: Event & { key?: string; value?: string } = new Event(
        "localUpdated"
      )
      event.key = key
      event.value = valueToStore

      window.localStorage.setItem(key, JSON.stringify(valueToStore))
      window.dispatchEvent(event)

      setState(value)
    } catch (error) {
      console.log(error)
    }
  }

  // Handle custom events for watching changes
  useEffect(() => {
    if (watch) {
      const handleStorageChange = (e: Event) => {
        const event = e as Event & { key?: string; value?: string }
        if (event.key === key) {
          setState(event.value)
        }
      }
      window.addEventListener("localUpdated", handleStorageChange)
      return () => {
        window.removeEventListener("localUpdated", handleStorageChange)
      }
    }
  }, [watch, key])

  return [state, setValue]
}
